import { useEffect, useCallback } from 'react';

interface ProgressData {
  currentView: string;
  hasStarted: boolean;
  questionId: string;
  progress: number;
  currentFrequency: number;
  minFreq: number;
  maxFreq: number;
  timestamp: number;
}

const STORAGE_KEY = 'tinnitusTune_progress';
const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours

export const useProgressSave = () => {
  const loadProgress = useCallback((): ProgressData | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return null;

      const data = JSON.parse(saved) as ProgressData;
      
      // Check if data is expired
      if (Date.now() - data.timestamp > EXPIRY_TIME) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to load progress:', error);
      return null;
    }
  }, []);

  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear progress:', error);
    }
  }, []);

  const saveProgress = useCallback((data: Partial<ProgressData>) => {
    try {
      const currentData = loadProgress();
      const newData: ProgressData = {
        currentView: 'assessment',
        hasStarted: false,
        questionId: '1',
        progress: 0,
        currentFrequency: 1000,
        minFreq: 20,
        maxFreq: 20000,
        ...currentData,
        ...data,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [loadProgress]);

  const hasExistingProgress = useCallback((): boolean => {
    const progress = loadProgress();
    return progress !== null && progress.hasStarted;
  }, [loadProgress]);

  // Auto-save on page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const progress = loadProgress();
      if (progress && progress.hasStarted && !progress.currentView.includes('result')) {
        e.preventDefault();
        e.returnValue = 'You have an assessment in progress. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [loadProgress]);

  return {
    saveProgress,
    loadProgress,
    clearProgress,
    hasExistingProgress,
  };
};